import {announce} from '@github-ui/aria-live'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {fromAhpChatState} from '@github-ui/copilot-conversation-export/ahp-adapter'
import {downloadConversation} from '@github-ui/copilot-conversation-export/download'
import type {ExportFormat} from '@github-ui/copilot-conversation-export/types'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import type {ChatState} from '@microsoft/agent-host-protocol'
import {useCallback, useEffect} from 'react'

import type {ChatDisplayIdentity} from '../types'

import {usePersistedTaskTitle} from './use-persisted-task-title'
import {
  useNativeAhpSession,
  useNativeAhpTimelineFallbackState,
  useThreadTitleActions,
  useThreadTitleState,
} from './use-timeline-contexts'

/**
 * Picks the `ChatState` to export from. The relay state is authoritative once
 * provisioned, but a generated title can arrive while its turns are still empty
 * during the handoff, so we only trust the relay for export once it actually has
 * turns; otherwise we keep the local optimistic conversation so the user's first
 * turn isn't dropped.
 */
export function resolveExportState(
  relayState: ChatState | undefined,
  localState: ChatState | undefined,
): ChatState | undefined {
  const relayHasTurns = relayState != null && (relayState.turns.length > 0 || relayState.activeTurn != null)
  return relayHasTurns ? relayState : (localState ?? relayState)
}

export function pickDisplayableTitle(title: string | undefined, sessionId: string | undefined): string | undefined {
  const trimmed = title?.trim()
  if (!trimmed) return undefined
  if (sessionId && trimmed === sessionId) return undefined
  return trimmed
}

/**
 * Resolves the conversation title and export handler for the header from whichever
 * AHP session is currently authoritative, keeping that source-selection logic
 * out of the presentational shell.
 *
 * Title priority (highest → lowest):
 *   1. `threadTitleState` — a user-set title from the header's rename affordance
 *      wins while its host acknowledgement is pending.
 *   2. The canonical `sessionState.title` for the primary chat.
 *   3–4. The live relay `state.title` and CAPI-persisted `task.name`. Live relay
 *      state normally wins. Restored embedded sessions can prefer the durable
 *      task name because a reconnect snapshot may expose sandbox metadata.
 *   5. The local optimistic `state.title` — covers the brief pre-provisioning
 *      window before the relay snapshot arrives.
 *   6. A `'Chat'` default.
 *
 */
export function useConversationHeaderState(
  ahpSession: ChatDisplayIdentity | undefined,
  {preferPersistedTitle = false, apiURL}: {preferPersistedTitle?: boolean; apiURL?: string} = {},
): {
  title: string
  exportConversation: ((format: ExportFormat) => Promise<void>) | undefined
} {
  const runtimeAhpSession = useNativeAhpSession()
  const activeAhpSession = ahpSession ?? runtimeAhpSession
  const establishedSession = useOptionalEstablishedAhpSession()
  const establishedView = useOptionalEstablishedAhpSessionViewState()
  const {state: localState} = useNativeAhpTimelineFallbackState()
  const relayState = establishedSession ? establishedView.state : localState
  const threadTitleState = useThreadTitleState()
  const threadTitle = threadTitleState.title?.trim() || undefined
  const {onSessionRenamed} = useThreadTitleActions()
  const persistedTitle = usePersistedTaskTitle(activeAhpSession?.taskId, apiURL)

  const sessionId = activeAhpSession?.sessionId ?? establishedSession?.identity.sessionId
  const relayTitle = pickDisplayableTitle(relayState?.title, sessionId)
  const sessionState = establishedSession ? establishedView.sessionState : undefined
  const isPrimaryChat = !relayState?.resource || relayState.resource === sessionState?.defaultChat
  const canonicalTitle = isPrimaryChat ? pickDisplayableTitle(sessionState?.title, sessionId) : undefined
  const taskId = activeAhpSession?.taskId ?? establishedSession?.identity.taskId
  useEffect(() => {
    if (taskId && canonicalTitle) onSessionRenamed?.(taskId, canonicalTitle)
  }, [taskId, canonicalTitle, onSessionRenamed])
  const title =
    (threadTitleState.userSet ? threadTitle : undefined) ??
    canonicalTitle ??
    threadTitle ??
    (preferPersistedTitle ? (persistedTitle ?? relayTitle) : (relayTitle ?? persistedTitle)) ??
    pickDisplayableTitle(localState?.title, sessionId) ??
    'Chat'

  const exportState = resolveExportState(relayState, localState)

  // Reuse the resolved title (and current user) so the export matches the header.
  // Quick Chat has no stable per-conversation URL, so `url` is omitted rather than
  // fabricated from the page location.
  const exportConversation = useCallback(
    async (format: ExportFormat) => {
      if (!exportState) return
      try {
        await downloadConversation(
          fromAhpChatState(exportState, {title, user: getCurrentUserLogin() || undefined}),
          format,
        )
      } catch {
        announce('The conversation could not be exported.')
      }
    },
    [exportState, title],
  )

  return {title, exportConversation: exportState ? exportConversation : undefined}
}
